import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import WhatsAppIcon from '@/components/homepage/icons/WhatsAppIcon';
import { X, Users, Zap, TrendingUp, Award } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useReferralPlan } from '@/hooks/useReferralPlan';

const FloatingButtons: React.FC = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isButtonCollapsed, setIsButtonCollapsed] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const plan = useReferralPlan();

  // Show popup after 5 seconds on every page load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsPopupOpen(true);
      setIsButtonCollapsed(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  // Scroll detection for footer visibility (desktop only)
  useEffect(() => {
    if (isMobile) return; // Skip on mobile

    const handleScroll = () => {
      const footer = document.querySelector('footer');
      if (!footer) return;

      const footerRect = footer.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Check if any part of the footer is visible in the viewport
      const footerIsVisible = footerRect.top < windowHeight && footerRect.bottom > 0;
      
      setIsFooterVisible(footerIsVisible);
    };

    window.addEventListener('scroll', handleScroll);
    // Check initial state
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Update button collapsed state based on footer visibility (desktop only)
  useEffect(() => {
    if (isMobile) return; // Skip on mobile

    if (isFooterVisible) {
      setIsButtonCollapsed(true);
    } else if (!isPopupOpen) {
      // Only expand if popup is not open
      setIsButtonCollapsed(false);
    }
  }, [isFooterVisible, isMobile, isPopupOpen]);

  const handleContactClick = () => {
    navigate('/contact-us');
    setIsPopupOpen(false);
  };

  const handleButtonClick = () => {
    if (!isPopupOpen) {
      setIsPopupOpen(true);
      setIsButtonCollapsed(true);
    }
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    // On mobile, keep button collapsed; on desktop, expand it only if footer is not visible
    if (isMobile) {
      setIsButtonCollapsed(true);
    } else {
      setIsButtonCollapsed(isFooterVisible);
    }
  };

  return (
    <div className="fixed bottom-2 right-2 z-50">
      {/* Popup Window */}
      {isPopupOpen && (
        <div 
          className={`absolute ${isMobile ? 'bottom-2 right-0 w-80' : 'bottom-2 right-0 w-96'} bg-gray-900 border border-purple-500/30 rounded-2xl shadow-xl transform transition-all duration-300 animate-fade-in-fast subtle-glow`}
        >
          {/* Close Button */}
          <button
            onClick={handleClosePopup}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-200 transition-colors duration-200 z-10"
          >
            <X className="h-6 w-6" />
          </button>

          <div className={`${isMobile ? 'p-4' : 'p-6'}`}>
            {/* Logo */}
            <div className="flex justify-center mb-4">
              <img
                src="/logo_1_inverted_no_background.png"
                alt="The Club Logo"
                className={`${isMobile ? 'h-10' : 'h-12'} w-auto`}
              />
            </div>

            {/* Title with Icon */}
            <div className="flex items-center justify-center mb-2">
              <Award className="h-6 w-6 text-yellow-500 mr-2" />
              <h3 className={`text-white font-bold ${isMobile ? 'text-base' : 'text-base'} text-center`}>
                {t('floatingButton.embassador.title')}
              </h3>
            </div>

            {/* Main Content */}
            <p className={`text-gray-300 ${isMobile ? 'text-xs' : 'text-sm'} mb-3 leading-relaxed text-center`}>
              {t('floatingButton.embassador.text')}
            </p>

            {/* Feature Highlights */}
            <div className={`space-y-3 ${isMobile ? 'mb-4' : 'mb-4'}`}>
              <div className={`flex items-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-300`}>
                <Users className="h-4 w-4 text-blue-500 mr-3 flex-shrink-0" />
                <span>{t('floatingButton.embassador.feature1')}</span>
              </div>
              <div className={`flex items-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-300`}>
                <TrendingUp className="h-4 w-4 text-green-500 mr-3 flex-shrink-0" />
                <span>{t('floatingButton.embassador.feature2').replace(/\{maxLevels\}/g, String(plan.maxLevels))}</span>
              </div>
              <div className={`flex items-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-300`}>
                <Zap className="h-4 w-4 text-yellow-500 mr-3 flex-shrink-0" />
                <span>{t('floatingButton.embassador.feature3')}</span>
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={handleContactClick}
              className={`w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white ${isMobile ? 'py-2 px-3' : 'py-3 px-4'} rounded-xl transition-all duration-300 flex items-center justify-center gap-2 font-medium transform hover:scale-105 shadow-lg`}
            >
              <WhatsAppIcon className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-white`} />
              <span className={isMobile ? 'text-sm' : 'text-base'}>
                {t('floatingButton.embassador.contact')}
              </span>
            </button>
          </div>
        </div>
      )}
      
      {/* Main Ambassador Button */}
      <div className="relative flex justify-end">
        {(isButtonCollapsed || isMobile) ? (
          /* Collapsed Icon Button - now with shimmer effect */
          <button
            onClick={handleButtonClick}
            className={`${isMobile ? 'w-12 h-12' : 'w-14 h-14'} bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-full transition-all duration-300 flex items-center justify-center shadow-lg transform hover:scale-110 relative overflow-hidden`}
          >
            {/* Continuous shimmer effect for collapsed button */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 animate-shimmer"></div>
            
            <Users className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'} relative z-10`} />
          </button>
        ) : (
          /* Full Button - only show on desktop when not collapsed */
          <button
            onClick={handleButtonClick}
            className={`bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white ${isMobile ? 'py-3 px-4' : 'py-5 px-6'} rounded-full transition-all duration-300 flex items-center justify-center gap-2 shadow-lg transform hover:scale-105 relative overflow-hidden`}
          >
            {/* Continuous shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 animate-shimmer"></div>
            
            <Users className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} relative z-10`} />
            <span className={`relative z-10 font-medium ${isMobile ? 'text-sm' : 'text-base'}`}>
              {t('floatingButton.embassador.button')}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default FloatingButtons;